import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { ReponseDoublon } from "@sfs/contracts";
import { useTitre } from "@shared/hooks/useTitre";
import { prospectionApi } from "./api";
import { useSuiviAnalyse } from "./hooks";
import { SuiviEtapes } from "./components/SuiviEtapes";

export function PageProspection() {
  useTitre("Prospection");
  const navigate = useNavigate();
  const [url, setUrl] = useState("");
  const [doublon, setDoublon] = useState<ReponseDoublon | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [urlsLot, setUrlsLot] = useState("");

  const { data: travail } = useSuiviAnalyse(runId);

  async function analyser(forcer = false) {
    setErreur(null);
    try {
      if (!forcer) {
        // détection de doublon AVANT de lancer : à un million de fiches,
        // le doublon est la première source de pollution
        const d = await prospectionApi.verifier(url);
        if (d.doublon) { setDoublon(d); return; }
      }
      const r = await prospectionApi.lancer(url, forcer);
      setDoublon(null);
      setRunId(r.scrapeRunId);
    } catch (e) { setErreur((e as Error).message); }
  }

  // Contrairement à "Créer une fiche distincte" (forcerCreation), on rescrape
  // le prospect déjà identifié : il faut donc court-circuiter la vérification
  // de doublon, sinon on retombe systématiquement sur la même alerte.
  async function rafraichir() {
    if (!doublon?.prospectId) return;
    setErreur(null);
    try {
      const r = await prospectionApi.lancer(url, false, doublon.prospectId);
      setDoublon(null);
      setRunId(r.scrapeRunId);
    } catch (e) { setErreur((e as Error).message); }
  }

  return (
    <main className="centre">
      <div className="panel">
        <h2 className="blocktitle">Nouveau prospect</h2>
        <label className="f" htmlFor="url">URL du site à analyser</label>
        <div className="ligne">
          <input id="url" type="text" value={url} placeholder="https://exemple.fr"
                 onChange={e => setUrl(e.target.value)}
                 onKeyDown={e => e.key === "Enter" && analyser()} />
          <button className="btn" onClick={() => analyser()} disabled={!url}>Analyser</button>
        </div>
        <p className="mut petit">
          Sources publiques uniquement. Le robots.txt est vérifié avant toute collecte ;
          les pages protégées par authentification sont ignorées.
        </p>
        {erreur && <p className="erreur">{erreur}</p>}
      </div>

      {doublon?.doublon && (
        <div className="alerte">
          <strong>Ce domaine existe déjà en base</strong> — {doublon.raisonSociale}, analysé il y a {doublon.joursDepuisScrape} jours.
          <div className="ligne" style={{ marginTop: 10 }}>
            <button className="btn sm" onClick={() => navigate(`/prospects/${doublon.prospectId}`)}>
              Ouvrir la fiche existante
            </button>
            <button className="btn ghost sm" onClick={rafraichir}>Rafraîchir</button>
            <button className="btn ghost sm" onClick={() => analyser(true)}>Créer une fiche distincte</button>
          </div>
        </div>
      )}

      {travail && <SuiviEtapes travail={travail} onOuvrir={id => navigate(`/prospects/${id}`)} />}

      <div className="panel">
        <h2 className="blocktitle">Analyse par lot</h2>
        <p className="mut petit">
          Jusqu'à 500 URL, une par ligne. Le lot passe en file avec un délai entre requêtes
          pour ne pas surcharger les sites cibles.
        </p>
        <textarea rows={4} value={urlsLot} onChange={e => setUrlsLot(e.target.value)}
                  placeholder={"https://site-1.fr\nhttps://site-2.fr"} />
        <button className="btn ghost sm" style={{ marginTop: 10 }}
                onClick={async () => {
                  const urls = urlsLot.split("\n").map(u => u.trim()).filter(Boolean);
                  if (urls.length) { await prospectionApi.lot(urls); setUrlsLot(""); }
                }}>
          Mettre en file
        </button>
      </div>
    </main>
  );
}
